import React from "react";
import PropTypes from "prop-types";
import { Box, Tooltip, Typography } from "@mui/material";
import { SIDEBAR } from "./sidebarVisual";

/** Compact Pin-style summary metric: label + value + optional detail. */
export default function SummaryMetricCard({ label, value, detail, mutedSurface = false }) {
  return (
    <Box
      sx={{
        border: SIDEBAR.border,
        borderRadius: SIDEBAR.radius,
        bgcolor: mutedSurface ? SIDEBAR.surfaceReadonly : SIDEBAR.surface,
        boxShadow: mutedSurface ? "none" : SIDEBAR.shadow,
        px: SIDEBAR.cardPadX,
        py: SIDEBAR.metrics.summaryPadY,
        minWidth: 0,
        minHeight: SIDEBAR.metrics.summaryMinHeight,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      data-testid={`summary-metric-${label}`}
    >
      <Typography sx={{ ...SIDEBAR.type.label, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          ...SIDEBAR.type.value,
          fontSize: SIDEBAR.metrics.summaryValueSize,
          flex: 1,
          display: "flex",
          alignItems: "center",
          py: SIDEBAR.gap.labelToValue,
        }}
      >
        {value}
      </Typography>
      {detail ? (
        <Typography sx={{ ...SIDEBAR.type.meta, flexShrink: 0 }}>
          {detail}
        </Typography>
      ) : (
        <Box sx={{ ...SIDEBAR.type.meta, visibility: "hidden", flexShrink: 0 }} aria-hidden>
          —
        </Box>
      )}
    </Box>
  );
}

SummaryMetricCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  detail: PropTypes.node,
  mutedSurface: PropTypes.bool,
};

export function TagChip({ label, sx: sxProp, ...props }) {
  return (
    <Box
      component="span"
      data-testid="company-tag-chip"
      {...props}
      sx={[
        (theme) => {
          const isDark = theme.palette.mode === "dark";
          return {
            // Same language as size-column chips, scaled up for the sidebar header.
            display: "inline-flex",
            alignItems: "center",
            height: 28,
            px: "10px",
            borderRadius: "8px",
            border: `1px solid ${
              isDark ? "rgba(255, 255, 255, 0.18)" : "rgba(0, 0, 0, 0.12)"
            }`,
            bgcolor: isDark ? "rgba(255, 255, 255, 0.04)" : "#F7F7F8",
            fontSize: 12.5,
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1,
            whiteSpace: "nowrap",
          };
        },
        ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
      ]}
    >
      {label}
    </Box>
  );
}

TagChip.propTypes = {
  label: PropTypes.string.isRequired,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

const DEFAULT_VISIBLE_TAGS = 3;

/**
 * Shows up to `maxVisible` tags above the fold; remaining tags collapse into a
 * "+N more" chip with a hover tooltip listing the overflow.
 */
export function CompanyTagsRow({ tags = [], maxVisible = DEFAULT_VISIBLE_TAGS }) {
  if (!tags.length) return null;

  const visible = tags.slice(0, maxVisible);
  const overflow = tags.slice(maxVisible);

  return (
    <Box
      data-testid="company-tags-row"
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: SIDEBAR.gap.chip,
      }}
    >
      {visible.map((tag) => (
        <TagChip key={tag} label={tag} />
      ))}
      {overflow.length > 0 ? (
        <Tooltip
          arrow
          placement="top"
          title={
            <Box
              component="ul"
              data-testid="company-tags-overflow-tooltip"
              sx={{
                m: 0,
                pl: 1.75,
                pr: 0.5,
                py: 0.25,
                maxWidth: 280,
              }}
            >
              {overflow.map((tag) => (
                <Box
                  component="li"
                  key={tag}
                  sx={{ fontSize: 12, lineHeight: 1.45, mb: 0.25, "&:last-child": { mb: 0 } }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          }
        >
          <Box component="span" sx={{ display: "inline-flex", cursor: "default" }}>
            <TagChip
              label={`+${overflow.length} more`}
              data-testid="company-tags-more"
            />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
}

CompanyTagsRow.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string),
  maxVisible: PropTypes.number,
};
